import {
  type ReleaseSummaryWorkflow,
  type UserSummary,
  type WorkflowStep,
  type WorkflowStepInput,
  WorkflowStepStatuses,
} from "~/api/types";
import { type WorkflowStepData } from "~/api/v2/requests";

import { toISODateTimeString } from "./date";
import { currentUserAtTopCompareFn, printFullName } from "./user";

export const progressRatio = ({
  completedSteps,
  totalSteps,
}: ReleaseSummaryWorkflow) =>
  totalSteps === 0 ? 0 : completedSteps / totalSteps;

export function mapStepInputsToWorkflowSteps(
  steps: WorkflowStepInput[],
): WorkflowStep[] {
  return steps.map((s, i) => ({
    id: s.id ?? -(i + 1),
    name: s.name,
    description: s.description ?? "",
    type: s.type,
    dueAt: s.dueAt,
    order: i,
    status: WorkflowStepStatuses.upcoming,
    users: s.users,
  }));
}

export function mapStepToWorkflowStepData(
  step: Omit<WorkflowStepInput, "_key" | "order" | "status">,
): WorkflowStepData {
  return {
    id: step.id,
    name: step.name.trim(),
    description: step.description ?? null,
    type: step.type,
    dueAt: step.dueAt ? toISODateTimeString(step.dueAt) : null,
    users: step.users.map((user) => ({
      userId: user.userId,
      permission: user.permission,
    })),
  };
}

export function extractOwners(docs: UserSummary[], currentUserId: number) {
  const ownerIds = new Set<number>();

  return docs
    .filter(({ userId }) => {
      if (ownerIds.has(userId)) {
        return false;
      } else {
        ownerIds.add(userId);
        return true;
      }
    })
    .toSorted((a, b) =>
      currentUserAtTopCompareFn(a.userId, b.userId, currentUserId),
    )
    .map((owner) => ({
      label: printFullName(owner, owner.userId === currentUserId),
      value: `${owner.userId}`,
    }));
}
